// src/pages/Consultation/SessionDetail/SessionHeader.jsx
import React, { useState, useEffect } from "react";
import {
  FiPause,
  FiPlay,
  FiCheckCircle,
  FiUser,
  FiPhone,
  FiMail,
  FiClock,
  FiMapPin,
} from "react-icons/fi";
import Button from "../../../components/common/Button";

const SessionHeader = ({
  session,
  onPause,
  onResume,
  onComplete,
  resumingSession = false,
}) => {
  const [duration, setDuration] = useState(0);

  // Calculate live duration
  useEffect(() => {
    if (!session?.startTime) return;

    const calculateDuration = () => {
      const start = new Date(session.startTime);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000 / 60); // minutes
      setDuration(diff);
    };

    calculateDuration();

    // Only set interval if session is active
    if (session.status === "ACTIVE") {
      const interval = setInterval(calculateDuration, 60000); // Update every minute
      return () => clearInterval(interval);
    }
  }, [session?.startTime, session?.status]);

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "PAUSED":
        return "bg-yellow-100 text-yellow-800";
      case "COMPLETED":
        return "bg-blue-100 text-blue-800";
      case "CONVERTED":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!session) {
    return null;
  }

  const customerName =
    session.userId?.name || session.guestCustomer?.name || "Guest Customer";
  const customerPhone =
    session.userId?.phoneNumber || session.guestCustomer?.phoneNumber || "";
  const customerEmail =
    session.userId?.email || session.guestCustomer?.email || "";

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      {/* Top Bar */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {session.sessionCode}
              </h1>
              <span
                className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                  session.status
                )}`}
              >
                {session.status}
              </span>
              {session.status === "ACTIVE" && (
                <span className="flex items-center">
                  <span className="relative flex h-3 w-3 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                  <span className="text-sm text-gray-600">Live</span>
                </span>
              )}
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <FiClock />
                <span className="font-medium text-lg text-gray-900">
                  {formatDuration(duration)}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <FiMapPin />
                <span>{session.branchCode}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-400">Type:</span>
                <span>
                  {session.sessionType === "APPOINTMENT"
                    ? "📅 Appointment"
                    : "🚶 Walk-in"}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {(session.status === "ACTIVE" || session.status === "PAUSED") && (
              <>
                <Button
                  variant="outline"
                  onClick={session.status === "ACTIVE" ? onPause : onResume}
                  disabled={resumingSession}
                  loading={resumingSession && session.status === "PAUSED"}
                >
                  {session.status === "ACTIVE" ? (
                    <>
                      <FiPause className="mr-2" />
                      Pause
                    </>
                  ) : (
                    <>
                      <FiPlay className="mr-2" />
                      Resume
                    </>
                  )}
                </Button>

                <Button
                  onClick={onComplete}
                  disabled={
                    session.status === "COMPLETED" ||
                    session.status === "CONVERTED"
                  }
                >
                  <FiCheckCircle className="mr-2" />
                  Complete
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Customer & Admin Info */}
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Customer Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">
            Customer Information
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FiUser className="text-gray-400" />
              <span className="font-medium">{customerName}</span>
            </div>
            {customerPhone && (
              <div className="flex items-center gap-2">
                <FiPhone className="text-gray-400" />
                <a
                  href={`tel:${customerPhone}`}
                  className="text-primary hover:underline"
                >
                  {customerPhone}
                </a>
              </div>
            )}
            {customerEmail && (
              <div className="flex items-center gap-2">
                <FiMail className="text-gray-400" />
                <a
                  href={`mailto:${customerEmail}`}
                  className="text-primary hover:underline"
                >
                  {customerEmail}
                </a>
              </div>
            )}
            {session.customerId && (
              <div className="text-sm text-gray-600 mt-2">
                Customer ID: {session.customerId}
              </div>
            )}
          </div>
        </div>

        {/* Admin Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Session Admin</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FiUser className="text-gray-400" />
              <span className="font-medium">
                {session.primaryAdmin?.adminName || "N/A"}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Code: {session.primaryAdmin?.adminCode || "N/A"}
            </div>
            <div className="text-sm text-gray-600">
              Role: {session.primaryAdmin?.role || "N/A"}
            </div>
            {session.startTime && (
              <div className="text-sm text-gray-600 mt-2">
                Started: {new Date(session.startTime).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pause Information (if paused) */}
      {session.status === "PAUSED" && session.breaks?.length > 0 && (
        <div className="px-6 pb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FiPause className="text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-900">Session Paused</p>
                {session.breaks[session.breaks.length - 1]?.reason && (
                  <p className="text-sm text-yellow-700 mt-1">
                    Reason: {session.breaks[session.breaks.length - 1].reason}
                  </p>
                )}
                {session.breaks[session.breaks.length - 1]?.pausedAt && (
                  <p className="text-xs text-yellow-600 mt-2">
                    Paused at:{" "}
                    {new Date(
                      session.breaks[session.breaks.length - 1].pausedAt
                    ).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Summary Stats */}
      {(session.sessionCart || session.exchangeItems?.length > 0) && (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {session.sessionCart && (
              <>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 mb-1">Cart Items</p>
                  <p className="text-lg font-semibold text-blue-900">
                    {session.sessionCart.itemCount || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-green-600 mb-1">Cart Value</p>
                  <p className="text-lg font-semibold text-green-900">
                    ₹{(session.sessionCart.totalValue || 0).toLocaleString()}
                  </p>
                </div>
              </>
            )}
            {session.exchangeItems && session.exchangeItems.length > 0 && (
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-xs text-purple-600 mb-1">Exchange Items</p>
                <p className="text-lg font-semibold text-purple-900">
                  {session.exchangeItems.length}
                </p>
              </div>
            )}
            {session.duration !== undefined && (
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="text-xs text-orange-600 mb-1">Total Duration</p>
                <p className="text-lg font-semibold text-orange-900">
                  {formatDuration(session.duration)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionHeader;
